import { useState } from 'preact/hooks';
import { saveSettings } from '../lib/admin.js';
import { DEFAULT_SETTINGS } from '../lib/email.js';
import { useApp } from '../lib/app-state.js';
import { toast } from '../lib/server.js';

export default function SettingsPage() {
  const app = useApp();
  const [s, setS] = useState(app.settings);
  const [saving, setSaving] = useState(false);
  const gmail = app.config?.gmail || {};
  const set = (k) => (e) => setS({ ...s, [k]: e.currentTarget.value });

  const save = async () => {
    setSaving(true);
    try {
      const num = (v, d) => (Number.isFinite(parseFloat(v)) ? Math.max(0, parseFloat(v)) : d);
      const clean = {
        ...s,
        validDays: Math.max(0, parseInt(s.validDays, 10) || 0),
        shipStandard: num(s.shipStandard, DEFAULT_SETTINGS.shipStandard),
        shipFreeOver: num(s.shipFreeOver, DEFAULT_SETTINGS.shipFreeOver),
        activatorRatio: num(s.activatorRatio, DEFAULT_SETTINGS.activatorRatio) || DEFAULT_SETTINGS.activatorRatio,
        reducerRatio: num(s.reducerRatio, DEFAULT_SETTINGS.reducerRatio) || DEFAULT_SETTINGS.reducerRatio,
      };
      await saveSettings(app.shop.id, clean);
      app.setSettings(clean);
      toast('Settings saved');
    } catch (e) {
      toast(e.message, true);
    }
    setSaving(false);
  };

  return (
    <s-page heading="Settings" inlineSize="base">
      <s-button slot="primary-action" variant="primary" loading={saving} onClick={save}>Save</s-button>

      <s-section heading="Email account">
        {gmail.connected ? (
          <s-stack direction="inline" gap="base" alignItems="center">
            <s-badge tone="success">Connected</s-badge>
            <s-text>Emails go out from <s-text type="strong">{gmail.email}</s-text> through Gmail. They appear in its Sent folder and replies land in its inbox.</s-text>
          </s-stack>
        ) : (
          <s-stack gap="small">
            <s-stack direction="inline" gap="base" alignItems="center">
              <s-badge tone="warning">Not connected</s-badge>
              <s-text>Quotes go out as Shopify’s invoice email until Gmail is connected.</s-text>
            </s-stack>
            {gmail.error && <s-text tone="critical">{gmail.error}</s-text>}
            <s-paragraph color="subdued">Connecting is a one-time “Allow” in Google, done from the Mac (npm run connect:gmail in ~/code/afx-quotes).</s-paragraph>
          </s-stack>
        )}
      </s-section>

      <s-section heading="Quote email">
        <s-stack gap="base">
          <s-text-field label="From name" value={s.fromName} onInput={set('fromName')} details="Shown as the sender name." />
          <s-text-field label="Subject" value={s.subject} onInput={set('subject')} details="{number} = the quote number, {first} = the customer’s first name." />
          <s-text-field label="Greeting" value={s.greeting} onInput={set('greeting')} />
          <s-text-area label="Opening" rows={3} value={s.intro} onInput={set('intro')} />
          <s-text-area label="Closing" rows={2} value={s.closing} onInput={set('closing')} />
          <s-text-area label="Follow-up message" rows={4} value={s.followUp} onInput={set('followUp')} details="Used when you email the same quote again." />
          <s-text-area label="Signature" rows={4} value={s.signature} onInput={set('signature')} details="Added to every email, quotes and plain ones." />
          <s-number-field label="Prices held for (days)" min={0} value={String(s.validDays)} onInput={set('validDays')} details="Shown on the quote as “Prices held until …”. 0 hides it." />
          <s-stack direction="inline">
            <s-button variant="tertiary" onClick={() => setS({ ...DEFAULT_SETTINGS })}>Reset to defaults</s-button>
          </s-stack>
        </s-stack>
      </s-section>

      <s-section heading="Shipping">
        <s-stack gap="base">
          <s-grid gridTemplateColumns="1fr 1fr" gap="base">
            <s-money-field label="Standard shipping" value={String(s.shipStandard)} onInput={set('shipStandard')} />
            <s-money-field label="Free shipping from" value={String(s.shipFreeOver)} onInput={set('shipFreeOver')} details="0 = never free" />
          </s-grid>
          <s-text color="subdued">New quotes start with Standard shipping at this rule, the same as your store's General shipping profile. Any quote can type its own price.</s-text>
        </s-stack>
      </s-section>

      <s-section heading="Mixing">
        <s-grid gridTemplateColumns="1fr 1fr" gap="base">
          <s-number-field label="2K activator ratio (paint : activator)" min={1} step={0.5} value={String(s.activatorRatio)} onInput={set('activatorRatio')} suffix=": 1" details="SP150 with SPA150 is 4 : 1 — a gallon takes a quart." />
          <s-select label="Default 2K activator" value={s.activatorProduct || 'act'} onChange={set('activatorProduct')}>
            {[['act', app.addons?.act], ['act_ht', app.addons?.act_ht]].filter(([, a]) => a).map(([k, a]) => (
              <s-option key={k} value={k}>{a.title} — ${a.price.toFixed(2)}</s-option>
            ))}
          </s-select>
          <s-number-field label="1K reducer ratio (paint : reducer)" min={0.5} step={0.5} value={String(s.reducerRatio)} onInput={set('reducerRatio')} suffix=": 1" details="Sizes the reducer offered with 1K tins." />
        </s-grid>
      </s-section>

      <s-section heading="Paint">
        <s-stack gap="small">
          <s-text>
            Paint is priced from the store’s <s-text type="strong">{app.paint?.title || 'paint product'}</s-text> — the same variants, tiers and
            prices as the paint finder. Change a price there and quotes follow.
          </s-text>
          <s-text color="subdued">
            Colour data: {app.config?.paintBase ? 'the live theme’s paint finder files' : 'not found (search off — colours can still be entered by hand)'}.
          </s-text>
        </s-stack>
      </s-section>
    </s-page>
  );
}
